import { shipmentContactInputSchema } from "../shipment-contact.schema";
import { ShippingTextError } from "@/lib/shipping-text";
/**
 * Admin bulk-create form — port of customer-web `bulk-ship/bulk-ship-form.tsx`.
 *
 * Two adaptations from the source:
 *  1. UI re-skinned from @heroui/react → shadcn/Radix (`@/components/ui/*`).
 *  2. Self-scoped session reads (useSession / useMy*) → admin customer-scoped
 *     hooks taking the `customerId` prop of the customer the admin ships FOR.
 *
 * Flow: upload (CSV/xlsx or manual) → review rows (address / package / pickup /
 * payment) → quote → bulk-create (`POST /api/v2/orders/bulk-create`) → complete.
 *
 * v1-import seam: the imperative handle exposes `prefillRows(rows)` so the later
 * v1 prefill can push `ParsedOrder[]` straight into the review phase.
 */
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  MapPin,
  Plus,
  Truck,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { GetQuoteRequest, PricingOption } from "@/api.types.v2";
import { useCustomerContacts } from "@/features/address-book/api";
import { useGetCustomerPaymentMethods } from "@/api/customer-credit";
import { useGetCustomerPaymentTerms } from "@/api/payments";
import { useGetQuote } from "@/api/get-quote";
import {
  type BulkOrderResult,
  type ShipmentInput,
  type UnifiedOrderRequest,
  useBulkCreateOrders,
} from "@/api/unified-orders";
import {
  type BulkPickupConfig,
  type DimensionUnit,
  formatCurrency,
  formatPickupLabel,
  makeOrderId,
  makeQuoteId,
  orderChargeCents,
  type PackageEdit,
  type ParsedOrder,
  parseSpreadsheet,
  type PaymentChoice,
  type PickupMode,
  type RowPickup,
  selectDefaultQuote,
  totalChargeCents,
  type ValidatedAddress,
  validateRow,
  updateBulkShippingRow,
  mergeBulkShippingRow,
  type WeightUnit,
  downloadTemplate,
} from "./index";

// ────────────────────────────────────────────────────────────────────────────
// Local helpers
// ────────────────────────────────────────────────────────────────────────────

/** Inline spinner (admin-web has no Spinner component). */
function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      className={cn("size-4 animate-spin text-muted-foreground", className)}
    />
  );
}

/** A fresh, empty manual-entry row. Incomplete → starts in `needs_fix`. */
function emptyRow(index: number): ParsedOrder {
  return {
    id: makeOrderId(index),
    recipientName: "",
    recipientPhone: "",
    recipientEmail: "",
    recipientAddress: "",
    recipientUnit: "",
    recipientCity: "",
    recipientProvince: "",
    recipientPostalCode: "",
    weight: 1,
    weightUnit: "kg",
    length: 30,
    width: 20,
    height: 15,
    dimensionUnit: "cm",
    deliveryNotes: "",
    status: "needs_fix",
    selected: true,
  };
}

/**
 * Map a row's user-entered fields into the `ValidatedAddress` shape the quote /
 * create steps consume. Admin port skips the customer-web Google geocoder step
 * (deferred): the server validates the address during get-quote, so lat/lng are
 * left at 0 and the raw fields flow straight through.
 */
function buildValidatedAddress(order: ParsedOrder): ValidatedAddress {
  return {
    street1: order.recipientAddress,
    street2: order.recipientUnit || undefined,
    city: order.recipientCity,
    state: order.recipientProvince,
    postalCode: order.recipientPostalCode,
    country: "CA",
    latitude: 0,
    longitude: 0,
  };
}

export interface BulkCreateFormHandle {
  /** v1-import seam: replace the rows and jump into the review phase. */
  prefillRows: (rows: ParsedOrder[]) => void;
}

interface BulkCreateFormProps {
  /** Customer the admin is bulk-creating orders FOR. */
  customerId: string;
  onClose?: () => void;
}

type Phase = "upload" | "review" | "complete";

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export const BulkCreateForm = forwardRef<
  BulkCreateFormHandle,
  BulkCreateFormProps
>(function BulkCreateForm({ customerId, onClose }, ref) {
  // Customer-scoped reads (admin adaptation of useMy* / useSession).
  const { data: contactsData, isLoading: isLoadingContacts } =
    useCustomerContacts(customerId);
  const contacts = useMemo(() => contactsData?.contacts ?? [], [contactsData]);
  const { data: billingData } = useGetCustomerPaymentMethods(customerId);
  const { data: paymentTermsData } = useGetCustomerPaymentTerms(customerId);

  // Net-terms data. Admin's CustomerPaymentTermsInfo has no `hasNetTermsAccess`
  // flag — derive it from whether any terms exist.
  const paymentTerms = paymentTermsData?.paymentTerms ?? [];
  const hasNetTermsAccess = paymentTerms.length > 0;
  const availableCredit = paymentTermsData?.availableCredit ?? 0;
  const hasUnpaidInvoices = paymentTermsData?.hasUnpaidInvoices ?? false;
  const effectivePaymentTermsId = paymentTerms[0]?.id ?? null;

  // ── State ──────────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>("upload");
  const [orders, setOrders] = useState<ParsedOrder[]>([]);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [isFetchingQuotes, setIsFetchingQuotes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState<
    Record<string, BulkOrderResult>
  >({});

  // Selected sender address.
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );

  // Pickup configuration (synthesized from the first quote's first pickup tier).
  const [pickupConfig, setPickupConfig] = useState<BulkPickupConfig | null>(
    null
  );
  const [pickupMode, setPickupMode] = useState<PickupMode>("none");
  const [defaultPickup, setDefaultPickup] = useState<RowPickup>(null);
  const [editPickupForOrderId, setEditPickupForOrderId] = useState<
    string | null
  >(null);

  // Payment selection.
  const [paymentChoice, setPaymentChoice] =
    useState<PaymentChoice>("SAVED_CARD");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  // File upload state.
  const [isDragging, setIsDragging] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);

  // Pending per-row package edits (committed on "re-quote").
  const [packageEdits, setPackageEdits] = useState<Record<string, PackageEdit>>(
    {}
  );

  // Mutations.
  const getQuote = useGetQuote();
  const bulkCreate = useBulkCreateOrders();

  // ── Derived sender / payment selections ─────────────────────────────────────
  const defaultContact = contacts.find((c) => c.isDefault) ?? contacts[0];
  const selectedContact = selectedAddressId
    ? contacts.find((c) => c.id === selectedAddressId)
    : defaultContact;

  useEffect(() => {
    if (!selectedAddressId && defaultContact?.id) {
      setSelectedAddressId(defaultContact.id);
    }
  }, [defaultContact?.id, selectedAddressId]);

  const senderAddress = useMemo(
    () =>
      selectedContact
        ? {
            name: selectedContact.contactName || "",
            phone: selectedContact.contactPhone ?? "",
            email: selectedContact.contactEmail,
            street1: selectedContact.addressLine1 || "",
            street2: selectedContact.addressLine2 ?? "",
            city: selectedContact.addressCity || "",
            state: selectedContact.addressProvince || "",
            postalCode: selectedContact.addressPostalCode ?? "",
            country: selectedContact.addressCountry,
          }
        : null,
    [selectedContact]
  );

  const paymentMethods = billingData?.paymentMethods ?? [];
  const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);

  useEffect(() => {
    if (!selectedPaymentMethodId && defaultPaymentMethod?.id) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod?.id, selectedPaymentMethodId]);

  // Mode → per-row pickup sync (custom mode is user-owned, never overwritten).
  useEffect(() => {
    if (pickupMode === "none") {
      setOrders((prev) => prev.map((o) => ({ ...o, pickup: null })));
    } else if (pickupMode === "same") {
      setOrders((prev) =>
        prev.map((o) => ({ ...o, pickup: defaultPickup ?? null }))
      );
    }
  }, [pickupMode, defaultPickup]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const readyCount = orders.filter(
    (o) => o.status === "ready" && o.selectedQuote && o.selected
  ).length;
  const needsFixCount = orders.filter((o) => o.status === "needs_fix").length;
  const completedCount = orders.filter((o) => o.status === "completed").length;
  const fetchingQuoteCount = orders.filter(
    (o) =>
      o.status === "fetching_quote" ||
      (o.status === "ready" && !o.selectedQuote)
  ).length;
  const allSelectableOrders = orders.filter(
    (o) => o.status === "ready" && o.selectedQuote
  );
  const allSelected =
    allSelectableOrders.length > 0 &&
    allSelectableOrders.every((o) => o.selected);
  const totalCost = totalChargeCents(orders);

  // ── Quote fetching ──────────────────────────────────────────────────────────
  const fetchQuotesForOrders = useCallback(
    async (ordersToQuote: ParsedOrder[]) => {
      if (!senderAddress) return;

      setIsFetchingQuotes(true);

      for (const order of ordersToQuote) {
        if (!order.validatedAddress) continue;
        const va = order.validatedAddress;

        let result: ParsedOrder;
        try {
          const request: GetQuoteRequest = {
            shipFrom: {
              id: "",
              line1: senderAddress.street1,
              line2: senderAddress.street2 || undefined,
              city: senderAddress.city,
              province: senderAddress.state,
              postalCode: senderAddress.postalCode,
              country: senderAddress.country,
              name: senderAddress.name,
              phone: senderAddress.phone || undefined,
              createdAt: "",
              updatedAt: "",
            },
            shipTo: {
              id: "",
              line1: va.street1,
              line2: va.street2,
              city: va.city,
              province: va.state,
              postalCode: va.postalCode,
              country: va.country,
              name: order.recipientName,
              phone: order.recipientPhone || undefined,
              email: order.recipientEmail || undefined,
              createdAt: "",
              updatedAt: "",
            },
            packages: [
              {
                weight: order.weight,
                weightUnit: order.weightUnit,
                length: order.length,
                width: order.width,
                height: order.height,
                dimensionUnit: order.dimensionUnit,
              },
            ],
            customerId,
          };

          const response = await getQuote.mutateAsync(request);

          // Capture pickupConfig once (sender shared → derive from first tier
          // with slots).
          if (!pickupConfig) {
            const firstTier = response.options
              .flatMap((o) => o.pickupOptions)
              .find((tier) => tier.timeSlots.length > 0);
            if (firstTier) setPickupConfig({ timeSlots: firstTier.timeSlots });
          }

          const options = response.options;
          const selectedQuote = selectDefaultQuote(options);
          result = selectedQuote
            ? {
                ...order,
                status: "ready",
                availableQuotes: options,
                selectedQuote,
                quoteId: makeQuoteId(selectedQuote),
              }
            : {
                ...order,
                status: "needs_fix",
                errorMessage: "No shipping options available",
              };
        } catch (error) {
          console.error("Error fetching quote for order:", order.id, error);
          result = {
            ...order,
            status: "needs_fix",
            errorMessage:
              error instanceof ShippingTextError
                ? error.message
                : "Could not price this shipment",
          };
        }
        setOrders((current) => mergeBulkShippingRow(current, order, result));
      }

      setIsFetchingQuotes(false);
    },
    [senderAddress, customerId, getQuote, pickupConfig]
  );

  // Trigger quote fetch when sender address becomes available and rows wait.
  useEffect(() => {
    if (!senderAddress || isFetchingQuotes) return;
    const waiting = orders.filter((o) => o.status === "fetching_quote");
    if (waiting.length > 0) {
      void fetchQuotesForOrders(waiting);
    }
  }, [senderAddress, orders, isFetchingQuotes, fetchQuotesForOrders]);

  /**
   * Validate one row's fields; on success map → validatedAddress and queue it
   * for quoting (the effect above picks it up). On failure flag `needs_fix`.
   */
  const validateAndQuoteRows = useCallback((rows: ParsedOrder[]) => {
    setOrders((prev) =>
      prev.map((o) => {
        const row = rows.find((r) => r.id === o.id);
        if (!row) return o;
        const issues = validateRow(o);
        if (issues.length > 0) {
          return {
            ...o,
            status: "needs_fix",
            errorMessage: issues[0]?.message,
            validatedAddress: undefined,
            availableQuotes: undefined,
            selectedQuote: undefined,
            quoteId: undefined,
          };
        }
        return {
          ...o,
          status: "fetching_quote",
          errorMessage: undefined,
          validatedAddress: buildValidatedAddress(o),
        };
      })
    );
  }, []);

  // ── Upload handlers ─────────────────────────────────────────────────────────
  const handleFileUpload = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const parsed = parseSpreadsheet(data);
      if (parsed.length === 0) {
        toast.error("No orders found", {
          description: "The spreadsheet had no recognizable rows.",
        });
        return;
      }
      setOrders(parsed);
      setPhase("review");
      validateAndQuoteRows(parsed);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error("Could not read file", {
        description: "Make sure it is a valid CSV or Excel file.",
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && /\.(csv|xlsx|xls)$/i.test(file.name)) {
      void handleFileUpload(file);
    } else {
      setIsInvalid(true);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileUpload(file);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const openFilePicker = () => fileInputRef.current?.click();

  const startManualEntry = () => {
    const row = emptyRow(0);
    setOrders([row]);
    setExpandedOrderId(row.id);
    setPhase("review");
  };

  const addManualRow = () => {
    const row = emptyRow(orders.length);
    setOrders((prev) => [...prev, row]);
    setExpandedOrderId(row.id);
  };

  // ── prefillRows seam (v1-import) ────────────────────────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      prefillRows: (rows: ParsedOrder[]) => {
        setOrders(rows);
        setPhase("review");
        validateAndQuoteRows(rows);
      },
    }),
    [validateAndQuoteRows]
  );

  // ── Row mutations ───────────────────────────────────────────────────────────
  const updateOrderField = <K extends keyof ParsedOrder>(
    orderId: string,
    field: K,
    value: ParsedOrder[K]
  ) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? updateBulkShippingRow(o, field, value) : o
      )
    );
  };

  const toggleOrderSelection = (orderId: string) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, selected: !o.selected } : o))
    );
  };

  const toggleAllOrders = (selected: boolean) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.status === "ready" && o.selectedQuote ? { ...o, selected } : o
      )
    );
  };

  const selectQuoteForOrder = (orderId: string, quote: PricingOption) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? { ...o, selectedQuote: quote, quoteId: makeQuoteId(quote) }
          : o
      )
    );
  };

  // ── Package edit machinery (faithful to source) ─────────────────────────────
  const initPackageEdit = (order: ParsedOrder) => {
    if (!packageEdits[order.id]) {
      setPackageEdits((prev) => ({
        ...prev,
        [order.id]: {
          weight: order.weight,
          weightUnit: order.weightUnit,
          length: order.length,
          width: order.width,
          height: order.height,
          dimensionUnit: order.dimensionUnit,
          deliveryNotes: order.deliveryNotes,
        },
      }));
    }
  };

  const updatePackageEdit = (
    orderId: string,
    field: keyof PackageEdit,
    value: number | string
  ) => {
    setPackageEdits((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], [field]: value },
    }));
  };

  const hasPackageChanges = (order: ParsedOrder) => {
    const edits = packageEdits[order.id];
    if (!edits) return false;
    return (
      edits.weight !== order.weight ||
      edits.weightUnit !== order.weightUnit ||
      edits.length !== order.length ||
      edits.width !== order.width ||
      edits.height !== order.height ||
      edits.dimensionUnit !== order.dimensionUnit ||
      edits.deliveryNotes !== order.deliveryNotes
    );
  };

  const cancelPackageChanges = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    setPackageEdits((prev) => ({
      ...prev,
      [orderId]: {
        weight: order.weight,
        weightUnit: order.weightUnit,
        length: order.length,
        width: order.width,
        height: order.height,
        dimensionUnit: order.dimensionUnit,
        deliveryNotes: order.deliveryNotes,
      },
    }));
  };

  const confirmPackageChanges = (order: ParsedOrder) => {
    const edits = packageEdits[order.id];
    if (!edits) return;
    const updated: ParsedOrder = {
      ...order,
      weight: edits.weight,
      weightUnit: edits.weightUnit,
      length: edits.length,
      width: edits.width,
      height: edits.height,
      dimensionUnit: edits.dimensionUnit,
      deliveryNotes: edits.deliveryNotes,
    };
    setOrders((prev) => prev.map((o) => (o.id === order.id ? updated : o)));
    validateAndQuoteRows([updated]);
  };

  // ── Build + submit bulk-create request ──────────────────────────────────────
  const toUnifiedOrderRequest = (order: ParsedOrder): UnifiedOrderRequest => {
    const opt = order.selectedQuote;
    const va = order.validatedAddress;
    if (!opt || !va) {
      throw new Error(
        "Cannot build request for a row without a selected quote and validated address"
      );
    }
    const senderEntryId = selectedAddressId ?? defaultContact?.id;
    if (!senderAddress) throw new Error("Sender contact is required");
    const sender = shipmentContactInputSchema(senderAddress.country).parse(
      senderAddress
    );
    const recipient = shipmentContactInputSchema(va.country).parse({
      name: order.recipientName,
      phone: order.recipientPhone,
      email: order.recipientEmail,
    });

    const shipment: ShipmentInput = {
      senderName: sender.name,
      senderPhone: sender.phone,
      senderEmail: sender.email,
      ...(senderEntryId
        ? { shipFromAddressBookEntryId: senderEntryId }
        : {
            shipFrom: {
              street1: senderAddress?.street1 ?? "",
              street2: senderAddress?.street2 || undefined,
              city: senderAddress?.city ?? "",
              state: senderAddress?.state ?? "",
              postalCode: senderAddress?.postalCode ?? "",
              country: senderAddress?.country ?? "CA",
            },
          }),
      recipientName: recipient.name,
      recipientPhone: recipient.phone,
      recipientEmail: recipient.email,
      shipTo: {
        street1: va.street1,
        street2: va.street2,
        city: va.city,
        state: va.state,
        postalCode: va.postalCode,
        country: va.country,
      },
      packages: [
        {
          weight: order.weight,
          weightUnit: order.weightUnit,
          length: order.length,
          width: order.width,
          height: order.height,
          dimensionUnit: order.dimensionUnit,
        },
      ],
      deliveryNotes: order.deliveryNotes || undefined,
      // The id, not the price — the server bills the quote row it wrote (#533).
      selectedRate: {
        quoteId: opt.quoteId,
        carrierId: opt.carrier.id,
        carrierServiceCode: opt.carrierService.code,
        fromServiceAreaId: opt.fromServiceAreaId,
        toServiceAreaId: opt.toServiceAreaId,
        currency: opt.currency,
      },
      // Per-row pickup add-on. Bulk MVP auto-picks the first tier (no per-row
      // tier picker); server consolidates matching keys into one PickupBooking.
      ...(order.pickup && opt.pickupOptions.length > 0
        ? {
            pickup: {
              pickupServiceId: opt.pickupOptions[0].pickupServiceId,
              scheduledDate: order.pickup.date,
              slotId: order.pickup.slotId,
            },
          }
        : {}),
    };

    const paymentMethodType =
      paymentChoice === "SAVED_CARD"
        ? "SAVED_CARD"
        : paymentChoice === "INVOICE"
          ? "INVOICE"
          : "PAYMENT_LINK";

    return {
      placerCustomerId: customerId,
      shipments: [shipment],
      paymentMethodType,
      ...(paymentChoice === "INVOICE" && effectivePaymentTermsId
        ? { paymentTermsId: effectivePaymentTermsId }
        : {}),
    };
  };

  const submitBatch = async () => {
    const selectedRows = orders.filter(
      (o) => o.status === "ready" && o.selectedQuote && o.selected
    );
    if (selectedRows.length === 0) return;
    const sender =
      senderAddress &&
      shipmentContactInputSchema(senderAddress.country).safeParse(
        senderAddress
      );
    if (!sender?.success) {
      toast.error(
        "Update the sender contact: name and valid phone are required; email must be valid when supplied."
      );
      return;
    }
    const invalidRows = selectedRows.filter(
      (row) => validateRow(row).length > 0
    );
    if (invalidRows.length > 0) {
      setOrders((current) =>
        current.map((row) =>
          invalidRows.some((invalid) => invalid.id === row.id)
            ? {
                ...row,
                status: "needs_fix",
                errorMessage: validateRow(row)
                  .map((issue) => `${issue.field}: ${issue.message}`)
                  .join("; "),
              }
            : row
        )
      );
      toast.error("Correct recipient contacts before creating orders");
      return;
    }

    setIsSubmitting(true);
    setOrders((prev) =>
      prev.map((o) =>
        selectedRows.some((s) => s.id === o.id)
          ? { ...o, status: "processing" }
          : o
      )
    );

    try {
      const reqOrders = selectedRows.map(toUnifiedOrderRequest);
      const res = await bulkCreate.mutateAsync({
        orders: reqOrders,
        stopOnError: false,
      });

      const resultsById: Record<string, BulkOrderResult> = {};
      res.results.forEach((result, idx) => {
        const row = selectedRows[idx];
        if (row) resultsById[row.id] = result;
      });
      setSubmitResults(resultsById);

      setOrders((prev) =>
        prev.map((o) => {
          const result = resultsById[o.id];
          if (!result) return o;
          return result.success
            ? { ...o, status: "completed" }
            : {
                ...o,
                status: "error",
                errorMessage: result.error ?? "Order creation failed",
              };
        })
      );

      if (res.summary.failed > 0) {
        toast.error(
          `${res.summary.failed} of ${res.summary.total} order(s) failed`
        );
      } else {
        toast.success(`Created ${res.summary.successful} order(s)`);
      }
      setPhase("complete");
    } catch (error) {
      console.error("Bulk create error:", error);
      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;
      setOrders((prev) =>
        prev.map((o) =>
          o.status === "processing"
            ? {
                ...o,
                status: "error",
                errorMessage: message ?? "Order creation failed",
              }
            : o
        )
      );
      toast.error("Could not create orders", {
        description: message ?? "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isFetchingQuotes || fetchingQuoteCount > 0;

  // ────────────────────────────────────────────────────────────────────────────
  // Upload phase
  // ────────────────────────────────────────────────────────────────────────────
  if (phase === "upload") {
    return (
      <div className="flex flex-1 flex-col">
        <div className="border-b px-6 pt-6 pb-4">
          <h2 className="text-xl font-semibold">Bulk create orders</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload a spreadsheet of recipients, or enter rows manually.
          </p>
        </div>

        <div className="flex-1 overflow-auto px-6 py-8">
          <div className="mx-auto max-w-2xl">
            <div className="mb-6 flex justify-center gap-3">
              <Button variant="outline" onClick={() => void downloadTemplate()}>
                <Download className="size-4" />
                Download template
              </Button>
              <Button variant="outline" onClick={startManualEntry}>
                <Plus className="size-4" />
                Enter manually
              </Button>
            </div>

            <button
              type="button"
              aria-label="Drop a spreadsheet here or browse to upload"
              className={cn(
                "relative w-full rounded-lg border-2 border-dashed p-8 text-left transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : isInvalid
                    ? "border-destructive"
                    : "border-border"
              )}
              onClick={openFilePicker}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={handleFileInput}
              />
              <div className="flex flex-col items-center justify-center py-8">
                <FileSpreadsheet className="mb-4 size-12 text-muted-foreground" />
                <p className="mb-4 text-center text-muted-foreground">
                  Drop a spreadsheet here, or{" "}
                  <span className="text-primary hover:underline">browse</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports CSV, XLSX, XLS
                </p>
              </div>
              {isInvalid && (
                <p className="mt-2 text-center text-sm text-destructive">
                  Unsupported file. Use CSV or Excel.
                </p>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Complete phase
  // ────────────────────────────────────────────────────────────────────────────
  if (phase === "complete") {
    const completedOrders = orders.filter((o) => o.status === "completed");
    const totalPaid = completedOrders.reduce(
      (sum, o) => sum + orderChargeCents(o),
      0
    );

    return (
      <div className="flex flex-1 flex-col">
        <div className="flex-1 overflow-auto px-6 py-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="size-8 text-green-600" />
            </div>
            <h2 className="mb-2 text-2xl font-bold text-green-700">
              {completedOrders.length} order(s) created
            </h2>
            <p className="mb-6 text-muted-foreground">
              Total {formatCurrency(totalPaid)}
            </p>

            <Card className="mb-6 text-left">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {completedOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between border-b py-2 last:border-0"
                    >
                      <div>
                        <p className="font-medium">{order.recipientName}</p>
                        <p className="text-sm text-muted-foreground">
                          {order.validatedAddress?.city},{" "}
                          {order.validatedAddress?.state}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {submitResults[order.id]?.orderTrackingNumber ?? "—"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(orderChargeCents(order))}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Done
              </Button>
              <Button onClick={() => window.open("/orders", "_blank")}>
                View orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Review phase
  // ────────────────────────────────────────────────────────────────────────────
  const paymentSelectValue =
    paymentChoice === "SAVED_CARD" && selectedPaymentMethodId
      ? `card:${selectedPaymentMethodId}`
      : paymentChoice === "INVOICE"
        ? "invoice"
        : "decide-later";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="shrink-0 border-b px-6 pt-6 pb-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Review orders</h2>
          <div className="text-center">
            <p className="text-2xl font-bold">
              {completedCount}/{orders.length}
            </p>
            <p className="text-xs text-muted-foreground">completed</p>
          </div>
        </div>

        {/* Ship From selector */}
        <div className="flex items-center gap-3">
          <MapPin className="size-4 shrink-0 text-muted-foreground" />
          <span className="shrink-0 text-sm text-muted-foreground">
            Ship from
          </span>
          {contacts.length > 0 ? (
            <Select
              value={selectedAddressId ?? defaultContact?.id ?? ""}
              onValueChange={(v) => setSelectedAddressId(v)}
            >
              <SelectTrigger size="sm" className="w-full max-w-sm">
                <SelectValue placeholder="Select address" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {`${contact.contactName} - ${contact.addressCity}, ${contact.addressProvince}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : isLoadingContacts ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              Loading addresses…
            </div>
          ) : (
            <span className="text-sm text-amber-600">
              No saved addresses for this customer
            </span>
          )}
        </div>

        {/* Pickup picker */}
        <div className="mt-3 flex items-start gap-3">
          <Truck className="mt-1 size-4 shrink-0 text-muted-foreground" />
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-sm text-muted-foreground">Pickup</span>
              <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "none"}
                  onChange={() => setPickupMode("none")}
                />
                No pickup (drop-off)
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "same"}
                  onChange={() => setPickupMode("same")}
                  disabled={!pickupConfig}
                />
                Same pickup for all
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "custom"}
                  onChange={() => setPickupMode("custom")}
                  disabled={!pickupConfig}
                />
                Custom per row
              </label>
            </div>

            {pickupMode === "same" && pickupConfig && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={defaultPickup?.date ?? ""}
                  onChange={(e) =>
                    setDefaultPickup({
                      date: e.target.value,
                      slotId: defaultPickup?.slotId ?? "",
                    })
                  }
                  min={new Date().toISOString().split("T")[0]}
                  className="h-8 w-44 text-sm"
                />
                <Select
                  value={defaultPickup?.slotId ?? ""}
                  onValueChange={(v) =>
                    setDefaultPickup({
                      date: defaultPickup?.date ?? "",
                      slotId: v,
                    })
                  }
                >
                  <SelectTrigger size="sm" className="w-48">
                    <SelectValue placeholder="Time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickupConfig.timeSlots.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {`${s.name} (${s.startTime}–${s.endTime})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {pickupMode === "custom" && (
              <p className="text-xs text-muted-foreground">
                Use the Edit button on each row to set its pickup.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Order table */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="px-6 py-4">
          {isLoading && (
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              Getting prices…
            </div>
          )}

          {!isLoading && needsFixCount > 0 && (
            <div className="mb-4 flex items-center gap-2 text-sm text-amber-600">
              <AlertCircle className="size-4" />
              {needsFixCount} row(s) need attention
            </div>
          )}

          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Up to 100 orders per batch.
            </p>
            <Button variant="outline" size="sm" onClick={addManualRow}>
              <Plus className="size-4" />
              Add row
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="text-sm text-muted-foreground">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(c) => toggleAllOrders(c === true)}
                      disabled={allSelectableOrders.length === 0}
                    />
                  </TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Pickup</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <Fragment key={order.id}>
                    <TableRow
                      className={cn(
                        "cursor-pointer transition-colors hover:bg-muted/30",
                        order.status === "completed"
                          ? "bg-green-50"
                          : order.status === "needs_fix" ||
                              order.status === "error"
                            ? "bg-amber-50"
                            : ""
                      )}
                      onClick={() => {
                        if (expandedOrderId !== order.id)
                          initPackageEdit(order);
                        setExpandedOrderId(
                          expandedOrderId === order.id ? null : order.id
                        );
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {order.status === "pending" ||
                        order.status === "validating" ||
                        order.status === "fetching_quote" ? (
                          <Spinner />
                        ) : order.status === "ready" && order.selectedQuote ? (
                          <Checkbox
                            checked={order.selected}
                            onCheckedChange={() =>
                              toggleOrderSelection(order.id)
                            }
                          />
                        ) : order.status === "ready" ? (
                          <Spinner />
                        ) : order.status === "processing" ? (
                          <Spinner className="text-primary" />
                        ) : order.status === "completed" ? (
                          <CheckCircle2 className="size-4 text-green-600" />
                        ) : order.status === "needs_fix" ? (
                          <AlertCircle className="size-4 text-amber-600" />
                        ) : order.status === "skipped" ? (
                          <X className="size-4 text-muted-foreground" />
                        ) : (
                          <AlertCircle className="size-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">
                          {order.recipientName || "—"}
                        </p>
                        {order.recipientPhone && (
                          <p className="text-sm text-muted-foreground">
                            {order.recipientPhone}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {order.status === "needs_fix" ||
                        order.status === "error" ? (
                          <span className="text-amber-600">
                            {order.errorMessage ?? "Invalid address"}
                          </span>
                        ) : (
                          <span>
                            {order.validatedAddress?.city ??
                              order.recipientCity}
                            ,{" "}
                            {order.validatedAddress?.state ??
                              order.recipientProvince}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {order.weight}
                        {order.weightUnit}
                      </TableCell>
                      <TableCell
                        className="text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          {order.pickup ? (
                            <span className="text-foreground">
                              {formatPickupLabel(order.pickup, pickupConfig)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              Drop-off
                            </span>
                          )}
                          {pickupConfig &&
                            (order.selectedQuote?.pickupOptions.length ?? 0) >
                              0 && (
                              <Button
                                variant="ghost"
                                size="xs"
                                onClick={() =>
                                  setEditPickupForOrderId(order.id)
                                }
                              >
                                Edit
                              </Button>
                            )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {order.selectedQuote ? (
                          <div className="flex items-center justify-end gap-2">
                            {order.pickup &&
                            order.selectedQuote.pickupOptions.length > 0 ? (
                              <Zap className="size-4 text-primary" />
                            ) : (
                              <Truck className="size-4 text-muted-foreground" />
                            )}
                            <span className="font-medium">
                              {formatCurrency(orderChargeCents(order))}
                            </span>
                          </div>
                        ) : order.status === "fetching_quote" ||
                          order.status === "ready" ? (
                          <Spinner className="ml-auto" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {expandedOrderId === order.id ? (
                          <ChevronUp className="size-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="size-4 text-muted-foreground" />
                        )}
                      </TableCell>
                    </TableRow>

                    {expandedOrderId === order.id && (
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell colSpan={7} className="p-4">
                          <div className="grid grid-cols-3 gap-6">
                            {/* Column 1: Recipient */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                                Recipient
                              </h4>
                              <div>
                                <Label className="text-xs">Name</Label>
                                <Input
                                  value={order.recipientName}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientName",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Phone</Label>
                                <Input
                                  value={order.recipientPhone}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientPhone",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Email</Label>
                                <Input
                                  value={order.recipientEmail}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientEmail",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">
                                  Street address
                                </Label>
                                <Input
                                  value={order.recipientAddress}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientAddress",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Unit</Label>
                                <Input
                                  value={order.recipientUnit}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientUnit",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Optional"
                                  className="h-8"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">City</Label>
                                  <Input
                                    value={order.recipientCity}
                                    onChange={(e) =>
                                      updateOrderField(
                                        order.id,
                                        "recipientCity",
                                        e.target.value
                                      )
                                    }
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Province</Label>
                                  <Input
                                    value={order.recipientProvince}
                                    onChange={(e) =>
                                      updateOrderField(
                                        order.id,
                                        "recipientProvince",
                                        e.target.value
                                      )
                                    }
                                    className="h-8"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Postal code</Label>
                                <Input
                                  value={order.recipientPostalCode}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientPostalCode",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                            </div>

                            {/* Column 2: Package & notes */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                                Package
                              </h4>
                              {(() => {
                                const edits = packageEdits[order.id] ?? {
                                  weight: order.weight,
                                  weightUnit: order.weightUnit,
                                  length: order.length,
                                  width: order.width,
                                  height: order.height,
                                  dimensionUnit: order.dimensionUnit,
                                  deliveryNotes: order.deliveryNotes,
                                };
                                const wChanged =
                                  edits.weight !== order.weight ||
                                  edits.weightUnit !== order.weightUnit;
                                const lChanged = edits.length !== order.length;
                                const wdChanged = edits.width !== order.width;
                                const hChanged = edits.height !== order.height;
                                const duChanged =
                                  edits.dimensionUnit !== order.dimensionUnit;
                                const nChanged =
                                  edits.deliveryNotes !== order.deliveryNotes;
                                return (
                                  <>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label
                                          className={cn(
                                            "text-xs",
                                            wChanged && "text-amber-600"
                                          )}
                                        >
                                          Weight {wChanged && "*"}
                                        </Label>
                                        <div className="flex gap-1">
                                          <Input
                                            type="number"
                                            value={String(edits.weight ?? "")}
                                            onChange={(e) =>
                                              updatePackageEdit(
                                                order.id,
                                                "weight",
                                                Number(e.target.value)
                                              )
                                            }
                                            className={cn(
                                              "h-8 flex-1",
                                              wChanged &&
                                                "border-amber-400 bg-amber-50"
                                            )}
                                          />
                                          <Select
                                            value={edits.weightUnit}
                                            onValueChange={(v) =>
                                              updatePackageEdit(
                                                order.id,
                                                "weightUnit",
                                                v as WeightUnit
                                              )
                                            }
                                          >
                                            <SelectTrigger
                                              size="sm"
                                              className="w-16"
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="kg">
                                                kg
                                              </SelectItem>
                                              <SelectItem value="lb">
                                                lb
                                              </SelectItem>
                                              <SelectItem value="oz">
                                                oz
                                              </SelectItem>
                                              <SelectItem value="g">
                                                g
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                      <div>
                                        <Label
                                          className={cn(
                                            "text-xs",
                                            duChanged && "text-amber-600"
                                          )}
                                        >
                                          Dimension unit {duChanged && "*"}
                                        </Label>
                                        <Select
                                          value={edits.dimensionUnit}
                                          onValueChange={(v) =>
                                            updatePackageEdit(
                                              order.id,
                                              "dimensionUnit",
                                              v as DimensionUnit
                                            )
                                          }
                                        >
                                          <SelectTrigger
                                            size="sm"
                                            className="w-full"
                                          >
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="cm">
                                              cm
                                            </SelectItem>
                                            <SelectItem value="in">
                                              in
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label
                                          className={cn(
                                            "text-xs",
                                            lChanged && "text-amber-600"
                                          )}
                                        >
                                          L ({edits.dimensionUnit}){" "}
                                          {lChanged && "*"}
                                        </Label>
                                        <Input
                                          type="number"
                                          value={String(edits.length ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "length",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={cn(
                                            "h-8",
                                            lChanged &&
                                              "border-amber-400 bg-amber-50"
                                          )}
                                        />
                                      </div>
                                      <div>
                                        <Label
                                          className={cn(
                                            "text-xs",
                                            wdChanged && "text-amber-600"
                                          )}
                                        >
                                          W ({edits.dimensionUnit}){" "}
                                          {wdChanged && "*"}
                                        </Label>
                                        <Input
                                          type="number"
                                          value={String(edits.width ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "width",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={cn(
                                            "h-8",
                                            wdChanged &&
                                              "border-amber-400 bg-amber-50"
                                          )}
                                        />
                                      </div>
                                      <div>
                                        <Label
                                          className={cn(
                                            "text-xs",
                                            hChanged && "text-amber-600"
                                          )}
                                        >
                                          H ({edits.dimensionUnit}){" "}
                                          {hChanged && "*"}
                                        </Label>
                                        <Input
                                          type="number"
                                          value={String(edits.height ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "height",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={cn(
                                            "h-8",
                                            hChanged &&
                                              "border-amber-400 bg-amber-50"
                                          )}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <Label
                                        className={cn(
                                          "text-xs",
                                          nChanged && "text-amber-600"
                                        )}
                                      >
                                        Delivery instructions {nChanged && "*"}
                                      </Label>
                                      <Input
                                        value={edits.deliveryNotes}
                                        onChange={(e) =>
                                          updatePackageEdit(
                                            order.id,
                                            "deliveryNotes",
                                            e.target.value
                                          )
                                        }
                                        className={cn(
                                          "h-8",
                                          nChanged &&
                                            "border-amber-400 bg-amber-50"
                                        )}
                                      />
                                    </div>
                                  </>
                                );
                              })()}

                              {order.status === "needs_fix" ||
                              order.status === "error" ? (
                                <Button
                                  size="sm"
                                  className="mt-3"
                                  onClick={() => {
                                    const edits = packageEdits[order.id];
                                    const updated: ParsedOrder = edits
                                      ? {
                                          ...order,
                                          weight: edits.weight,
                                          weightUnit: edits.weightUnit,
                                          length: edits.length,
                                          width: edits.width,
                                          height: edits.height,
                                          dimensionUnit: edits.dimensionUnit,
                                          deliveryNotes: edits.deliveryNotes,
                                        }
                                      : order;
                                    setOrders((prev) =>
                                      prev.map((o) =>
                                        o.id === order.id ? updated : o
                                      )
                                    );
                                    validateAndQuoteRows([updated]);
                                  }}
                                >
                                  Get quote
                                </Button>
                              ) : order.validatedAddress &&
                                order.status === "ready" &&
                                hasPackageChanges(order) ? (
                                <div className="mt-3 flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      cancelPackageChanges(order.id)
                                    }
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => confirmPackageChanges(order)}
                                  >
                                    Update price
                                  </Button>
                                </div>
                              ) : null}
                            </div>

                            {/* Column 3: Shipping options */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                                Shipping option
                              </h4>
                              {order.availableQuotes &&
                              order.availableQuotes.length > 0 ? (
                                <div className="max-h-[300px] space-y-2 overflow-y-auto pr-2">
                                  {order.availableQuotes.map((quote) => {
                                    const pickupAvailable =
                                      quote.pickupOptions.length > 0;
                                    const cheapestTier = pickupAvailable
                                      ? quote.pickupOptions.reduce((m, tier) =>
                                          tier.pickupPriceCents <
                                          m.pickupPriceCents
                                            ? tier
                                            : m
                                        )
                                      : null;
                                    const pickupMinCharge = cheapestTier
                                      ? cheapestTier.pickupPriceCents +
                                        (cheapestTier.taxCents ?? 0)
                                      : 0;
                                    const labelCharge =
                                      quote.labelPriceCents +
                                      (quote.taxCents ?? 0);
                                    const key = makeQuoteId(quote);
                                    const selectedKey = order.selectedQuote
                                      ? makeQuoteId(order.selectedQuote)
                                      : null;
                                    return (
                                      <button
                                        type="button"
                                        key={key}
                                        className={cn(
                                          "w-full rounded-lg border p-2 text-left transition-colors",
                                          selectedKey === key
                                            ? "border-primary bg-primary/5"
                                            : "border-border hover:border-muted-foreground/40"
                                        )}
                                        onClick={() =>
                                          selectQuoteForOrder(order.id, quote)
                                        }
                                      >
                                        <div className="mb-1 flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <Truck className="size-4 shrink-0 text-muted-foreground" />
                                            <p className="text-xs font-medium">
                                              {quote.carrier.name} —{" "}
                                              {quote.carrierService.name}
                                            </p>
                                          </div>
                                          <p className="text-sm font-bold">
                                            {formatCurrency(labelCharge)}
                                          </p>
                                        </div>
                                        <div className="flex items-center justify-between pl-6 text-xs text-muted-foreground">
                                          <span>
                                            {quote.carrierService
                                              .estimatedDays != null
                                              ? `${quote.carrierService.estimatedDays} business day(s)`
                                              : "Same day"}
                                          </span>
                                          {pickupAvailable && (
                                            <span className="flex items-center gap-1 text-green-600">
                                              <Zap className="size-3" />
                                              {quote.pickupOptions.length > 1
                                                ? `Pickup from ${formatCurrency(pickupMinCharge)}`
                                                : `Pickup ${formatCurrency(pickupMinCharge)}`}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : order.status === "fetching_quote" ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Spinner />
                                  Loading options…
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  No options yet
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Bottom panel — payment & submit */}
      <div className="shrink-0 rounded-b-lg border-t bg-background">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium">
                {readyCount} order(s) ready • {formatCurrency(totalCost)}
              </p>
              {needsFixCount > 0 && (
                <p className="text-sm text-amber-600">
                  {needsFixCount} row(s) need fixes
                </p>
              )}
            </div>

            {/* Payment method selector */}
            <Select
              value={paymentSelectValue}
              onValueChange={(value) => {
                if (value === "decide-later") {
                  setPaymentChoice("DECIDE_LATER");
                  setSelectedPaymentMethodId(null);
                } else if (value === "invoice") {
                  setPaymentChoice("INVOICE");
                  setSelectedPaymentMethodId(null);
                } else if (value.startsWith("card:")) {
                  setPaymentChoice("SAVED_CARD");
                  setSelectedPaymentMethodId(value.replace("card:", ""));
                }
              }}
            >
              <SelectTrigger className="w-[240px]">
                {paymentChoice === "INVOICE" ? (
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <CreditCard className="size-4 shrink-0 text-muted-foreground" />
                )}
                <SelectValue placeholder="Payment method" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((pm) => (
                  <SelectItem key={`card:${pm.id}`} value={`card:${pm.id}`}>
                    {pm.isDefault
                      ? `•••• ${pm.last4} (default)`
                      : `•••• ${pm.last4}`}
                  </SelectItem>
                ))}
                <SelectItem value="decide-later">Decide later</SelectItem>
                {hasNetTermsAccess && !hasUnpaidInvoices && (
                  <SelectItem value="invoice">
                    {`Invoice (${formatCurrency(availableCredit)} credit)`}
                  </SelectItem>
                )}
                {hasNetTermsAccess && hasUnpaidInvoices && (
                  <SelectItem value="invoice" disabled>
                    Invoice — pay outstanding first
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => void submitBatch()}
                disabled={readyCount === 0 || isLoading || isSubmitting}
                className="min-w-[160px]"
              >
                {isSubmitting || isLoading ? (
                  <>
                    <Spinner className="mr-2 text-current" />
                    {isSubmitting ? "Creating…" : "Loading…"}
                  </>
                ) : (
                  `Create ${readyCount} shipment(s)`
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Per-row pickup edit dialog */}
      {editPickupForOrderId && pickupConfig && (
        <PickupEditDialog
          order={orders.find((o) => o.id === editPickupForOrderId) ?? null}
          pickupConfig={pickupConfig}
          onClose={() => setEditPickupForOrderId(null)}
          onSave={(pickup) => {
            setOrders((prev) =>
              prev.map((o) =>
                o.id === editPickupForOrderId ? { ...o, pickup } : o
              )
            );
            setEditPickupForOrderId(null);
          }}
        />
      )}
    </div>
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Per-row pickup edit dialog
// ────────────────────────────────────────────────────────────────────────────
interface PickupEditDialogProps {
  order: ParsedOrder | null;
  pickupConfig: BulkPickupConfig;
  onClose: () => void;
  onSave: (pickup: RowPickup) => void;
}

function PickupEditDialog({
  order,
  pickupConfig,
  onClose,
  onSave,
}: PickupEditDialogProps) {
  const [mode, setMode] = useState<"dropoff" | "pickup">(
    order?.pickup ? "pickup" : "dropoff"
  );
  const [date, setDate] = useState(order?.pickup?.date ?? "");
  const [slotId, setSlotId] = useState(order?.pickup?.slotId ?? "");

  const handleApply = () => {
    if (mode === "dropoff") {
      onSave(null);
      return;
    }
    if (!date || !slotId) return;
    onSave({ date, slotId });
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {order?.recipientName
              ? `Pickup for ${order.recipientName}`
              : "Pickup for row"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="rowPickupMode"
              className="mt-1"
              checked={mode === "dropoff"}
              onChange={() => setMode("dropoff")}
            />
            <div>
              <div className="text-sm font-medium">Drop-off (no pickup)</div>
              <div className="text-xs text-muted-foreground">
                The sender drops this package off — no pickup fee.
              </div>
            </div>
          </label>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="rowPickupMode"
              className="mt-1"
              checked={mode === "pickup"}
              onChange={() => setMode("pickup")}
            />
            <div className="flex-1">
              <div className="text-sm font-medium">Pickup at sender</div>
              {mode === "pickup" && (
                <div className="mt-2 space-y-2">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Time slot</Label>
                    <Select value={slotId} onValueChange={setSlotId}>
                      <SelectTrigger size="sm" className="w-full">
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {pickupConfig.timeSlots.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {`${s.name} (${s.startTime}–${s.endTime})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={mode === "pickup" && (!date || !slotId)}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
